import type {
  IEntityDataItem,
  ISelectItem,
  IEntitySelectItem,
  TOmitNamed,
} from '@/types/models'
import { ProductLevelIds } from '~/services/selectLists/productLevels'

// Product Category

export interface IProductCategory extends IEntityDataItem {
  descr: string
  numItems?: number
}

// Product Category Overview

export interface IProductCategoryOverview {
  productCategory: IProductCategory
  productSubcategories: IProductSubcategory[]
}

export interface IProductCategoryParentChildObject {
  parent: {
    entityName: 'productCategory'
    object: IEntitySelectItem
  }
  children: {
    entityName: 'productSubcategory'
    entityDataItems: IProductSubcategory[]
  }
}

// Product Subcategory

export interface IProductSubcategory extends IEntityDataItem {
  productCategory: IEntitySelectItem
  descr: string
  numItems?: number
}

// Product Subcategory Overview

export interface IProductSubcategoryOverview {
  productSubcategory: IProductSubcategory
  productGroups: IProductGroup[]
}

export interface IProductSubcategoryParentChildObject {
  parent: {
    entityName: 'productSubcategory'
    object: IEntitySelectItem
  }
  children: {
    entityName: 'productGroup'
    entityDataItems: IProductGroup[]
  }
}

// Product Group

export interface IProductGroup extends IEntityDataItem {
  productSubcategory: IEntitySelectItem
  descr: string
  numItems?: number
}

// Product Group Overview

export interface IProductGroupOverview {
  productGroup: IProductGroup
  products: TProduct[]
}

export interface IProductGroupParentChildObject {
  parent: {
    entityName: 'productGroup'
    object: IEntitySelectItem
  }
  children: {
    entityName: 'product'
    entityDataItems: TProduct[]
  }
}

// Product

export interface IProductBase extends IEntityDataItem {
  productGroup: IEntitySelectItem
  descr: string
  type: ISelectItem
  level: ISelectItem // immutable once set
  inKitOnly?: boolean
  status: ISelectItem
  fieldSet?: IEntitySelectItem
  tableFieldSets?: IEntitySelectItem[]
  orderDetailFieldSet?: IEntitySelectItem
  numDaysToLateAmber?: number
  numDaysToLateRed?: number
  numOpportunities?: number
  numOrders?: number
  numServices?: number
  numSuppliers: number
}

export interface IKitProduct extends IProductBase {
  level: {
    id: ProductLevelIds.KIT
    label: 'Kit'
  }
  doExcludeKitServiceWhenBuildingServices?: boolean
  doAllowNoSelectionFromSet?: boolean
  doAllowMultipleSelectionsFromSet?: boolean
  numKitItems?: number
}

export interface ISetKitProduct
  extends TOmitNamed<IKitProduct, 'doExcludeKitServiceWhenBuildingServices'> {
  doExcludeKitServiceWhenBuildingServices: true
}

export interface IKitButNotSetKitProduct
  extends TOmitNamed<IKitProduct, 'doExcludeKitServiceWhenBuildingServices'> {
  doExcludeKitServiceWhenBuildingServices: false
}

export interface IItemProduct extends IProductBase {
  level: {
    id: ProductLevelIds.ITEM
    label: 'Item'
  }
  keyIdentifierFieldName?: string | null
}

export type TProduct = IKitProduct | IItemProduct

export function isKitProduct(product: any): product is IKitProduct {
  return product?.level.id === ProductLevelIds.KIT
}

export function isSetKitProduct(product: any): product is ISetKitProduct {
  return (
    isKitProduct(product) &&
    product.doExcludeKitServiceWhenBuildingServices === true
  )
}

export function isKitButNotSetKitProduct(
  product: any
): product is IKitButNotSetKitProduct {
  return (
    isKitProduct(product) && !product.doExcludeKitServiceWhenBuildingServices
  )
}

export function isItemProduct(product: any): product is IItemProduct {
  return product?.level.id === ProductLevelIds.ITEM
}

export function isProduct(product: any): product is TProduct {
  return isKitProduct(product) || isItemProduct(product)
}

export function assertIsKitProduct(
  val: any,
  fn: string
): asserts val is IKitProduct {
  if (!isKitProduct(val)) {
    throw new Error(
      `${fn} thisField.value should be an IKitProduct, but it's not.`
    )
  }
}

export function assertIsProduct(val: any, fn: string): asserts val is TProduct {
  if (!isProduct(val)) {
    throw new Error(
      `${fn} thisField.value should be an TProduct, but it's not.`
    )
  }
}

// Product Overview

export interface IKitProductOverview {
  product: IKitProduct
  kitItems: IProductKitItem[]
}

export type TProductOverview = IKitProductOverview

// ProductKitItem

export interface IProductKitItem extends IEntityDataItem {
  parentProduct: IEntitySelectItem
  product: IItemProduct | IKitProduct
  isOptional?: boolean
  quantity?: TKitQuantity
}

type TMultiplier = `x${number}`
export type TKitQuantity = number | `${number}` | 'kit' | TMultiplier | '--'

// parentObjectForCreate

// export interface IProductParentObjectForCreate {
//   entityName: 'parentProduct'
//   object: TProduct
// }

export interface IProductParentObjectForCreate {
  entityName: 'parentProduct'
  object: IEntitySelectItem
}
